/**
 * Hover-prefetch registry for MAUD ERP modules.
 *
 * Each module's pages are code-split via `lazy(() => import(...))` in its
 * routes/config. The first navigation to a module therefore pays the cost of
 * downloading (and, in dev, compiling) that chunk before anything renders.
 *
 * `prefetchRoute(pathname)` is called on sidebar hover: it kicks off the dynamic
 * import for the module's landing page so the chunk is ready by click time.
 *
 * The import specifiers below MUST resolve to the SAME files the route configs
 * lazy-import, so Vite/Rollup dedupe them to the same chunk (no extra bytes).
 */

type Loader = () => Promise<unknown>;

interface PrefetchEntry {
  /** URL prefix that triggers this module's prefetch (longest match wins). */
  prefix: string;
  /** Landing chunk(s) loaded when the module opens. */
  load: Loader;
}

// Ordered longest-prefix-first so a more specific module wins over a shorter one.
const ENTRIES: PrefetchEntry[] = [
  { prefix: '/app/maud-erp/asset-management',      load: () => import('./asset-management/pages/AssetManagementDashboard') },
  { prefix: '/app/maud-erp/leases-and-agreements', load: () => import('./leases-and-agreements/pages/LeasesAgreementsDashboard') },
  { prefix: '/app/maud-erp/finance-management',    load: () => import('./finance-management/pages/FinanceManagementDashboard') },
  { prefix: '/app/maud-erp/display-devices',       load: () => import('./display-devices/pages/DDIntegratedDashboard') },
  { prefix: '/app/maud-erp/legal-case-management', load: () => import('./legal-case-management/pages/LegalCaseManagementDashboard') },
  { prefix: '/app/maud-erp/municipal-council',     load: () => import('./municipal-council/pages/CouncilMeetingsDashboard') },
  { prefix: '/app/maud-erp/employee-management',   load: () => import('./employee-management/masters/ulb-employees-dashboard') },
  { prefix: '/app/maud-erp/collections',           load: () => import('../../core/setup') },
  { prefix: '/app/maud-erp/portal-management',     load: () => import('../../../pages/apps/portal/index') },
].sort((a, b) => b.prefix.length - a.prefix.length);

// Each module's chunk is only fetched once per session.
const fetched = new Set<string>();

/**
 * Prefetch the chunk for whichever module the given path belongs to.
 * Safe to call repeatedly (deduped) and on any path (no-op if unmatched).
 */
export function prefetchRoute(pathname?: string | null): void {
  if (!pathname) return;
  const entry = ENTRIES.find((e) => pathname === e.prefix || pathname.startsWith(e.prefix + '/') || pathname.startsWith(e.prefix));
  if (!entry || fetched.has(entry.prefix)) return;
  fetched.add(entry.prefix);
  // Fire-and-forget; a failed prefetch must never surface to the user — the real
  // navigation will retry the import and show its own error if it genuinely fails.
  entry.load().catch(() => { fetched.delete(entry.prefix); });
}
